<script lang="ts">
  /**
   * One round of a drinking game in the Zonenchat (amendment 20), drawn in place of its
   * message's text: a roll of the Wabenwürfel, a vote of Heiß oder kalt, a Stundenwette on the
   * district's hottest cell, the honeycomb of a Waben-Bingo.
   *
   * What it shows is the server's view, which already leaves out what this person may not see —
   * the others' votes until the round is over. A tap goes straight to the server and draws its
   * answer; whatever the others do arrives with the chat's polling.
   */
  import { untrack } from 'svelte';
  import { COLUMNS, honeycomb } from '$domain/honeycomb.ts';
  import { FIELD_MAX, type Vote, VOTES, WAGERS, type Wager } from '$domain/party.ts';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeError, session } from './session.svelte.ts';
  import { type Move, type ZoneMessage, zone } from './zone.svelte.ts';

  type Props = { message: ZoneMessage };
  const { message }: Props = $props();

  /** A roll this young was made just now: the die tumbles before it lands. */
  const FRESH_MS = 30_000;
  /** Where the pips sit on the 3 × 3 grid of a die face, row by row. */
  const PIPS: Record<number, number[]> = {
    1: [4],
    2: [0, 8],
    3: [0, 4, 8],
    4: [0, 2, 6, 8],
    5: [0, 2, 4, 6, 8],
    6: [0, 2, 3, 5, 6, 8],
  };
  const VOTE_LABEL: Record<Vote, string> = { hot: '🔥 Heiß', cold: '🧊 Kalt' };
  const WAGER_LABEL: Record<Wager, string> = { hotter: '🔥 Heißer', colder: '🧊 Kälter' };
  /** How long a beer flies to the middle of the honeycomb, and how long the middle cheers. */
  const FLIGHT_MS = 1100;
  const CHEERS_MS = 2400;
  /** A bingo hexagon at its largest, and the smallest a narrow screen may make it before a row
      holds one fewer instead. */
  const HEX_MAX = 72;
  const HEX_MIN = 60;
  const HEX_GAP = 4;

  let busy = $state(false);
  let error = $state('');
  let notice = $state('');
  let now = $state(Date.now());
  let place = $state<string | undefined>();
  /** The bingo field whose words and buttons are shown under the board. */
  let selected = $state<string | null>(null);
  /** The next field this person writes. */
  let draft = $state('');
  /** Beers on their way to the middle, one for each field that has just counted. */
  let flights = $state<{ key: string; x: number; y: number; dx: number; dy: number }[]>([]);
  /** The middle says "Trink!" while this is set. */
  let cheering = $state(false);
  let cheersTimer: ReturnType<typeof setTimeout> | undefined;

  // Read once: the tumble is for a roll arriving, not for one scrolled past later.
  const fresh = untrack(() => Date.now() - Date.parse(message.createdAt) < FRESH_MS);
  const game = $derived(message.party);
  const admin = $derived(session.user?.role === 'admin');
  /** The width the honeycomb has to fit into: the card's, once it is known. */
  let room = $state(0);
  const board = $derived.by(() => {
    if (game?.kind !== 'bingo') return null;
    // Four in a row where they fit at a size that still holds the words — on a 390 px phone
    // the card is narrower than four full ones — and three, larger, where they do not.
    const across = (columns: number) => Math.floor((room - (columns - 1) * HEX_GAP) / columns);
    const columns = room === 0 || across(COLUMNS) >= HEX_MIN ? COLUMNS : COLUMNS - 1;
    const size = room === 0 ? HEX_MAX : Math.min(HEX_MAX, across(columns));
    return honeycomb(game.fields.length, size, HEX_GAP, columns);
  });
  const chosen = $derived(
    game?.kind === 'bingo' ? game.fields.find((field) => field.id === selected) : undefined,
  );

  // Every field that counts from now on sends a beer to the middle. The ones that had already
  // counted when the board was first drawn do not: the beer is for something happening, not
  // for opening the chat again.
  let counted: Set<string> | undefined;
  $effect(() => {
    if (game?.kind !== 'bingo' || board === null) return;
    const fields = game.fields;
    const layout = board;
    const before = counted;
    counted = new Set(fields.filter((field) => field.markedAt !== null).map((field) => field.id));
    if (before === undefined) return;
    untrack(() => {
      fields.forEach((field, index) => {
        const from = layout.fields[index];
        if (field.markedAt === null || before.has(field.id) || from === undefined) return;
        const key = `${field.id}-${Date.now()}`;
        const flight = {
          key,
          x: from.x,
          y: from.y,
          dx: layout.centre.x - from.x,
          dy: layout.centre.y - from.y,
        };
        flights = [...flights, flight];
        setTimeout(() => {
          flights = flights.filter((other) => other.key !== key);
        }, FLIGHT_MS);
        // The middle cheers as the beer arrives.
        setTimeout(() => {
          cheering = true;
          clearTimeout(cheersTimer);
          cheersTimer = setTimeout(() => (cheering = false), CHEERS_MS);
        }, FLIGHT_MS * 0.85);
      });
    });
  });

  // A clock for the countdowns, running only while something counts down.
  $effect(() => {
    const counting =
      (game?.kind === 'hotcold' && game.status === 'open') ||
      (game?.kind === 'bet' && (game.status === 'open' || game.status === 'running'));
    if (!counting) return;
    const timer = setInterval(() => (now = Date.now()), 1000);
    return () => clearInterval(timer);
  });

  // The wager's cell by name, from the same lookup as the chat's district.
  $effect(() => {
    if (game?.kind !== 'bet') return;
    const cell = game.cell;
    place = cachedPlace(cell);
    if (place !== undefined) return;
    let cancelled = false;
    void reverseGeocode(cell).then((name) => {
      if (!cancelled) place = name;
    });
    return () => {
      cancelled = true;
    };
  });

  function countdown(until: number): string {
    const seconds = Math.max(0, Math.ceil((until - now) / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  const clock = (at: number) =>
    new Date(at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const gdi = (value: number) =>
    value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  /** "@ana", "@ana und @ben", "@ana, @ben und @cleo". */
  function names(list: readonly string[]): string {
    const tagged = list.map((name) => `@${name}`);
    if (tagged.length < 2) return tagged.join('');
    return `${tagged.slice(0, -1).join(', ')} und ${tagged.at(-1)}`;
  }

  const drink = (list: readonly string[]) => (list.length === 1 ? 'trinkt' : 'trinken');

  async function run(action: () => Promise<void>) {
    busy = true;
    error = '';
    try {
      await action();
    } catch (cause) {
      error = describeError(cause);
    } finally {
      busy = false;
    }
  }

  const play = (move: Move) => run(() => zone.move(message.id, move));

  async function write(event: SubmitEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (text === '') return;
    await play({ text });
    if (error === '') draft = '';
  }

  async function remove(id: string) {
    await play({ remove: id });
    if (error === '') selected = null;
  }

  async function report(id: string) {
    notice = '';
    await play({ report: id });
    if (error === '') notice = 'Gemeldet. Melden es zwei, verschwindet das Feld.';
  }
</script>

{#if game?.kind === 'dice'}
  <div class="card dice" class:fresh>
    <div class="die" aria-label={`Gewürfelt: ${game.face}`}>
      {#each Array.from({ length: 9 }, (_, index) => index) as spot (spot)}
        <span class="spot" class:pip={PIPS[game.face]?.includes(spot)}></span>
      {/each}
    </div>
    <div>
      <p class="title">Wabenwürfel: {game.face}</p>
      <p class="task">{game.task}</p>
    </div>
  </div>
{:else if game?.kind === 'hotcold'}
  <div class="card">
    <p class="title"><span aria-hidden="true">🔥🧊</span> Heiß oder kalt?</p>
    {#if game.status === 'open'}
      <p class="hint">Stimmt geheim ab – die Minderheit trinkt.</p>
      <div class="choices">
        {#each VOTES as choice (choice)}
          <button
            class:chosen={game.mine === choice}
            disabled={busy}
            onclick={() => play({ vote: choice })}
          >
            {VOTE_LABEL[choice]}
          </button>
        {/each}
      </div>
      <p class="state">
        {game.votes}
        {game.votes === 1 ? 'Stimme' : 'Stimmen'} ·
        {game.endsAt > now ? `noch ${countdown(game.endsAt)}` : 'wird ausgewertet …'}
        {#if game.mine !== null}· du: {VOTE_LABEL[game.mine]}{/if}
      </p>
      {#if game.asked}
        <button class="quiet" disabled={busy} onclick={() => play({ close: true })}>
          Jetzt auflösen
        </button>
      {/if}
    {:else if game.result !== null}
      {@const result = game.result}
      <p class="tally">🔥 {result.hot} : {result.cold} 🧊</p>
      <p class="state act">
        {#if result.drinks.length === 2}
          Gleichstand – alle trinken!
        {:else if result.drinks.length === 1}
          {result.drinks[0] === 'hot' ? 'Heiß' : 'Kalt'} ist in der Minderheit:
          {names(result.drinkers)}
          {drink(result.drinkers)}.
        {:else if result.hot + result.cold < 2}
          Zu wenige Stimmen – keiner muss trinken.
        {:else}
          Einstimmig – keiner muss trinken.
        {/if}
      </p>
      {#if game.mine !== null && result.drinks.includes(game.mine)}
        <p class="you">Prost – du trinkst. 🍻</p>
      {/if}
    {/if}
    {#if error}<p class="error" role="status">{error}</p>{/if}
  </div>
{:else if game?.kind === 'bet'}
  <div class="card">
    <p class="title"><span aria-hidden="true">⏳</span> Stundenwette</p>
    <p class="hint">
      {place ?? 'Die heißeste Wabe im Viertel'} · GDI {gdi(game.start)} um
      {clock(Date.parse(message.createdAt))}
    </p>
    {#if game.status === 'open'}
      <p class="state">Wird sie bis {clock(game.endsAt)} heißer oder kälter?</p>
      <div class="choices">
        {#each WAGERS as choice (choice)}
          <button
            class:chosen={game.mine === choice}
            disabled={busy}
            onclick={() => play({ wager: choice })}
          >
            {WAGER_LABEL[choice]}
          </button>
        {/each}
      </div>
      <p class="state">Wetten noch {countdown(game.closesAt)}</p>
    {:else if game.status === 'running'}
      <p class="state">
        Läuft bis {clock(game.endsAt)} – Meldungen von Leuten mit Einsatz zählen nicht.
      </p>
    {:else if game.status === 'expired'}
      <p class="state">Niemand hat gewettet.</p>
    {:else if game.verdict === null}
      <p class="state">Wird ausgewertet …</p>
    {:else}
      <p class="tally">
        {WAGER_LABEL[game.verdict]} geworden: GDI {gdi(game.start)} → {gdi(game.end ?? 0)}
      </p>
      <p class="state act">
        {#if game.drinkers.length > 0}
          {names(game.drinkers)} {drink(game.drinkers)}.
        {:else}
          Alle lagen richtig – keiner trinkt.
        {/if}
      </p>
    {/if}
    {#if game.wagers.length > 0 && game.status !== 'expired'}
      <ul class="wagers">
        {#each WAGERS as side (side)}
          {@const those = game.wagers.filter((entry) => entry.wager === side)}
          {#if those.length > 0}
            <li>{WAGER_LABEL[side]}: {names(those.map((entry) => entry.username))}</li>
          {/if}
        {/each}
      </ul>
    {/if}
    {#if error}<p class="error" role="status">{error}</p>{/if}
  </div>
{:else if game?.kind === 'bingo' && board !== null}
  <div class="card">
    <p class="title"><span aria-hidden="true">🎱</span> Waben-Bingo</p>
    <p class="hint">
      Jeder schreibt bis zu zwei Felder: was heute passieren könnte. Bestätigen es zwei
      Mitspieler, trinken alle.
    </p>

    {#if game.fields.length > 0}
      <!-- Measured, so that the honeycomb can be made to fit the card it is drawn in. -->
      <div bind:clientWidth={room}>
        <div
          class="board"
          role="group"
          aria-label="Bingo-Wabe"
          style:width="{board.width}px"
          style:height="{board.height}px"
          style:--hex-w="{board.hex.width}px"
          style:--hex-h="{board.hex.height}px"
        >
          <span
            class="hex centre"
            class:cheering
            role="img"
            aria-label={cheering ? 'Trink!' : 'Die Mitte'}
            style:left="{board.centre.x}px"
            style:top="{board.centre.y}px"
          >
            <span aria-hidden="true">🍺</span>{#if cheering}<strong>Trink!</strong>{/if}
          </span>
          {#each game.fields as field, index (field.id)}
            {@const slot = board.fields[index]}
            {#if slot !== undefined}
              <button
                class="hex"
                class:mine={field.mine}
                class:half={field.markedAt === null && field.confirmations > 0}
                class:counted={field.markedAt !== null}
                class:selected={selected === field.id}
                style:left="{slot.x}px"
                style:top="{slot.y}px"
                aria-label={`${field.text}, von @${field.author}${field.markedAt !== null ? ', gezählt' : `, ${field.confirmations} von 2 bestätigt`}`}
                onclick={() => (selected = selected === field.id ? null : field.id)}
              >
                <span class="text">{field.text}</span>
                {#if field.markedAt !== null}<small>✓</small>{:else if field.confirmations > 0}<small
                    >{field.confirmations}/2</small
                  >{/if}
              </button>
            {/if}
          {/each}
          {#each flights as flight (flight.key)}
            <span
              class="flight"
              aria-hidden="true"
              style:left="{flight.x}px"
              style:top="{flight.y}px"
              style:--dx="{flight.dx}px"
              style:--dy="{flight.dy}px">🍺</span
            >
          {/each}
        </div>
      </div>
    {/if}

    {#if chosen !== undefined}
      <div class="chosen">
        <p class="said">„{chosen.text}“ <small>von @{chosen.author}</small></p>
        <p class="state">
          {chosen.markedAt !== null
            ? `✓ Gezählt um ${clock(chosen.markedAt)}`
            : chosen.confirmed
              ? 'Du hast bestätigt – es fehlt noch einer.'
              : `${chosen.confirmations} von 2 haben bestätigt.`}
        </p>
        <div class="actions">
          {#if game.status === 'running' && chosen.markedAt === null && !chosen.confirmed}
            {#if game.playing}
              <button class="confirm" disabled={busy} onclick={() => play({ confirm: chosen.id })}>
                ✓ Ist passiert
              </button>
            {:else}
              <span class="state">Schreib ein eigenes Feld, dann kannst du bestätigen.</span>
            {/if}
          {/if}
          {#if chosen.mine || admin}
            <button class="quiet" disabled={busy} onclick={() => remove(chosen.id)}>Löschen</button>
          {/if}
          {#if !chosen.mine}
            <button class="quiet" disabled={busy} onclick={() => report(chosen.id)}>Melden</button>
          {/if}
        </div>
      </div>
    {/if}

    {#if game.status === 'running'}
      {#if game.free > 0}
        <form class="write" onsubmit={write}>
          <input
            bind:value={draft}
            maxlength={FIELD_MAX}
            placeholder={game.playing ? 'Noch ein Feld …' : 'Was passiert heute?'}
            aria-label="Neues Bingo-Feld"
          />
          <button type="submit" disabled={busy || draft.trim() === ''}>Anlegen</button>
        </form>
      {/if}
      <p class="state">
        {game.players}
        {game.players === 1 ? 'spielt' : 'spielen'} mit{game.free > 0
          ? ` · noch ${game.free} von 2 Feldern frei`
          : ''} · endet spätestens um {clock(game.endsAt)}
      </p>
    {:else}
      <p class="state act">
        {game.full
          ? '🎉 Volle Wabe! Alle trinken – die Runde ist vorbei.'
          : 'Feierabend – die drei Stunden sind um.'}
      </p>
    {/if}
    {#if notice}<p class="state" role="status">{notice}</p>{/if}
    {#if error}<p class="error" role="status">{error}</p>{/if}
  </div>
{/if}

<style>
  .card {
    margin-top: 0.3rem;
    padding: 0.6rem 0.75rem 0.65rem;
    border: 1px solid var(--border);
    border-radius: 0.75rem;
    background: color-mix(in srgb, var(--accent) 5%, var(--card));
  }

  p {
    margin: 0;
  }

  .title {
    font-size: 0.9375rem;
    font-weight: 600;
  }

  .hint,
  .state {
    margin-top: 0.3rem;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .state.act,
  .you {
    color: var(--fg);
    font-weight: 600;
  }

  .you {
    margin-top: 0.2rem;
    font-size: 0.875rem;
  }

  .tally {
    margin-top: 0.4rem;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .choices {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  .choices button {
    flex: 1;
    padding: 0.5rem 0.4rem;
    border: 1px solid color-mix(in srgb, var(--accent) 45%, var(--border));
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;
  }

  .choices button.chosen {
    border-color: var(--accent);
    background: color-mix(in srgb, var(--accent) 22%, var(--bg));
  }

  .quiet {
    margin-top: 0.35rem;
    padding: 0.1rem 0;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.75rem;
    text-decoration: underline;
    cursor: pointer;
  }

  .error {
    margin-top: 0.3rem;
    font-size: 0.8125rem;
    color: var(--accent);
  }

  .wagers {
    display: flex;
    flex-wrap: wrap;
    gap: 0.15rem 0.8rem;
    margin: 0.4rem 0 0;
    padding: 0;
    list-style: none;
    font-size: 0.8125rem;
  }

  /* Wabenwürfel: a hexagonal die, the pips on its 3 × 3 grid. */
  .dice {
    display: flex;
    gap: 0.8rem;
    align-items: center;
  }

  .die {
    display: grid;
    flex: none;
    grid-template-columns: repeat(3, 0.62rem);
    grid-template-rows: repeat(3, 0.62rem);
    gap: 0.22rem;
    place-content: center;
    width: 3.6rem;
    height: 4.15rem;
    background: var(--accent);
    clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
  }

  .spot.pip {
    border-radius: 50%;
    background: #fff;
  }

  .task {
    margin-top: 0.2rem;
    font-size: 0.9375rem;
  }

  .fresh .die {
    animation: tumble 0.9s cubic-bezier(0.3, 1.4, 0.5, 1);
  }

  .fresh .task {
    animation: land 0.4s ease-out 0.8s both;
  }

  /* Waben-Bingo: the players' fields as a honeycomb, a beer in the middle. */
  .board {
    position: relative;
    margin: 0.6rem auto 0.3rem;
  }

  .hex {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.1rem;
    width: var(--hex-w);
    height: var(--hex-h);
    padding: 0.95rem 0.4rem;
    border: 0;
    background: color-mix(in srgb, var(--fg) 9%, var(--card));
    color: var(--fg);
    font: inherit;
    font-size: 0.6rem;
    line-height: 1.12;
    text-align: center;
    clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
    cursor: pointer;
  }

  .hex .text {
    display: -webkit-box;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
  }

  .hex small {
    font-size: 0.6rem;
    font-weight: 700;
  }

  .hex.mine {
    background: color-mix(in srgb, var(--accent) 12%, var(--card));
  }

  /* One of two confirmations: the hexagon is half full. */
  .hex.half {
    background: linear-gradient(
      to top,
      color-mix(in srgb, var(--accent) 38%, var(--card)) 50%,
      color-mix(in srgb, var(--fg) 9%, var(--card)) 50%
    );
  }

  .hex.counted {
    background: var(--accent);
    color: #fff;
    font-weight: 600;
    animation: count 0.6s ease-out;
  }

  .hex.selected {
    filter: brightness(1.25) saturate(1.1);
  }

  .hex.centre {
    background: color-mix(in srgb, #f5b81c 30%, var(--card));
    font-size: 1.6rem;
    cursor: default;
  }

  .hex.centre strong {
    font-size: 0.75rem;
    color: #1a1300;
  }

  .hex.centre.cheering {
    background: #f5b81c;
    animation: cheers 0.6s ease-in-out 4;
  }

  /* A beer on its way from the field that just counted to the middle. */
  .flight {
    position: absolute;
    display: grid;
    place-items: center;
    width: var(--hex-w);
    height: var(--hex-h);
    font-size: 1.7rem;
    pointer-events: none;
    animation: fly 1.1s cubic-bezier(0.5, -0.3, 0.6, 1) forwards;
  }

  .chosen {
    margin-top: 0.45rem;
    padding: 0.45rem 0.55rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
  }

  .said {
    font-size: 0.875rem;
    overflow-wrap: anywhere;
  }

  .said small {
    color: var(--muted);
    font-size: 0.75rem;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.8rem;
    align-items: center;
    margin-top: 0.35rem;
  }

  .actions .quiet {
    margin-top: 0;
  }

  .confirm {
    padding: 0.35rem 0.8rem;
    border: 1px solid var(--accent);
    border-radius: 0.6rem;
    background: color-mix(in srgb, var(--accent) 16%, var(--bg));
    color: var(--fg);
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
  }

  .write {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }

  .write input {
    flex: 1;
    min-width: 0;
    padding: 0.4rem 0.55rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font: inherit;
    font-size: 0.875rem;
  }

  .write button {
    padding: 0.4rem 0.8rem;
    border: 1px solid var(--accent);
    border-radius: 0.6rem;
    background: color-mix(in srgb, var(--accent) 16%, var(--bg));
    color: var(--fg);
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .write button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @keyframes tumble {
    from {
      transform: rotate(-540deg) scale(0.3);
      opacity: 0;
    }
  }

  @keyframes land {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
  }

  @keyframes count {
    from {
      transform: scale(0.75);
    }
  }

  @keyframes fly {
    0% {
      opacity: 0;
      transform: translate(0, 0) scale(0.5);
    }
    18% {
      opacity: 1;
      transform: translate(0, 0) scale(1.35);
    }
    100% {
      opacity: 0.15;
      transform: translate(var(--dx), var(--dy)) scale(0.9);
    }
  }

  @keyframes cheers {
    0%,
    100% {
      transform: rotate(0) scale(1);
    }
    25% {
      transform: rotate(-7deg) scale(1.12);
    }
    75% {
      transform: rotate(7deg) scale(1.12);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .fresh .die,
    .fresh .task,
    .hex.counted,
    .hex.centre.cheering {
      animation: none;
    }

    .flight {
      display: none;
    }
  }
</style>
